/**
 * VistoeraTASK-00678 — Platform Overview "Worker 状态" table sort helpers.
 *
 * Frontend-only derived ordering. Does not mutate the source Worker array.
 * No backend / API / heartbeat changes.
 */

import type { AdminWorkerRow } from '../types'

export type OverviewWorkerSortKey = 'name' | 'status'
export type OverviewNameSortOrder = 'asc' | 'desc'
export type OverviewStatusSortOrder = 'online_first' | 'offline_first'

export type OverviewWorkerSort =
  | { key: 'name'; order: OverviewNameSortOrder }
  | { key: 'status'; order: OverviewStatusSortOrder }

export const DEFAULT_OVERVIEW_WORKER_SORT: OverviewWorkerSort = { key: 'name', order: 'asc' }

/** Display label used for sorting — same fallback as the Overview name cell. */
export function overviewWorkerSortName(row: AdminWorkerRow): string {
  const name = typeof row.name === 'string' ? row.name.trim() : ''
  return name || row.worker_id
}

/**
 * Case-insensitive natural compare (PC2 before PC10).
 * Uses the platform Collator numeric option — no extra dependency.
 */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * Online bucket for status sort.
 * `online` / `busy` count as online; `offline` / `draining` / `disabled` do not.
 * Unknown `display_status` is never treated as online (falls back to `online`
 * boolean only when display_status is absent).
 */
export function isOverviewWorkerOnlineBucket(row: AdminWorkerRow): boolean {
  const ds = row.display_status
  if (ds === 'online' || ds === 'busy') return true
  if (ds === 'offline' || ds === 'draining' || ds === 'disabled') return false
  if (ds == null || ds === '') return row.online === true
  return false
}

function compareByNameAsc(a: AdminWorkerRow, b: AdminWorkerRow): number {
  const byName = naturalCompare(overviewWorkerSortName(a), overviewWorkerSortName(b))
  if (byName !== 0) return byName
  return a.worker_id.localeCompare(b.worker_id)
}

export function compareOverviewWorkers(a: AdminWorkerRow, b: AdminWorkerRow, sort: OverviewWorkerSort): number {
  if (sort.key === 'name') {
    const cmp = compareByNameAsc(a, b)
    return sort.order === 'asc' ? cmp : -cmp
  }
  const aOnline = isOverviewWorkerOnlineBucket(a)
  const bOnline = isOverviewWorkerOnlineBucket(b)
  if (aOnline !== bOnline) {
    if (sort.order === 'online_first') return aOnline ? -1 : 1
    return aOnline ? 1 : -1
  }
  // Same online/offline bucket — always natural name ascending.
  return compareByNameAsc(a, b)
}

/** Returns a new array; never mutates `workers`. */
export function sortOverviewWorkers(workers: readonly AdminWorkerRow[], sort: OverviewWorkerSort): AdminWorkerRow[] {
  return workers.slice().sort((a, b) => compareOverviewWorkers(a, b, sort))
}

export function cycleOverviewNameSort(current: OverviewWorkerSort): OverviewWorkerSort {
  if (current.key === 'name' && current.order === 'asc') return { key: 'name', order: 'desc' }
  return { key: 'name', order: 'asc' }
}

export function cycleOverviewStatusSort(current: OverviewWorkerSort): OverviewWorkerSort {
  if (current.key === 'status' && current.order === 'online_first') {
    return { key: 'status', order: 'offline_first' }
  }
  return { key: 'status', order: 'online_first' }
}
